import React from "react";
import { Controller, type Control } from "react-hook-form";
import { DatePicker, TimePicker } from "@src/common/components";
import type { Stop } from "@api/graphql/generated/generated-types";
import StopCard from "@src/common/components/StopCard/StopCard";
import {
  dateToTimeString,
  timeStringToDate,
} from "@src/common/utils/timezoneConverters";
import type { ManualArrivalFormData } from "../types";
import { getManualArrivalReferenceHint } from "../utils/getManualArrivalReferenceHint";

interface TaskArrivalRowProps {
  stop: Stop;
  task: NonNullable<Stop["tasks"]>[number];
  control: Control<ManualArrivalFormData>;
}

export function TaskArrivalRow({ stop, task, control }: TaskArrivalRowProps) {
  const referenceHint = getManualArrivalReferenceHint(task, stop);

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[2fr,1fr,1fr] gap-4 items-start">
        <div>
          <StopCard stop={stop} />
        </div>
        <div className="flex flex-col z-50">
          <Controller
            control={control}
            name={`${task.id}.date`}
            render={({ field }) => (
              <>
                <DatePicker
                  size="mini"
                  value={field.value}
                  formatString="MMM do, yyyy"
                  onChange={({ date }) => field.onChange(date)}
                />
                {referenceHint && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-snug">
                    {referenceHint}
                  </div>
                )}
              </>
            )}
          />
        </div>
        <div className="flex flex-col z-40">
          <Controller
            control={control}
            name={`${task.id}.time`}
            render={({ field }) => (
              <TimePicker
                size="mini"
                nullable
                value={dateToTimeString(field.value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value === "") {
                    field.onChange(null);
                  } else {
                    field.onChange(timeStringToDate(event.target.value));
                  }
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
